import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getEmployeeRates, createEmployeeRate, deleteEmployeeRate } from '../../services/api';
import { formatDate, formatEur } from '../../lib/workStatus';
import { Trash2, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const today = () => new Date().toISOString().split('T')[0];

export const EmployeeRatesTab = ({ employees, workTypes }) => {
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ employee_id: '', work_type: '', rate: '', valid_from: today(), note: '' });
  const [saving, setSaving] = useState(false);
  const activeTypes = workTypes.filter((w) => w.active);

  const load = useCallback(async () => {
    try {
      setRates(await getEmployeeRates(filterEmployee === 'all' ? {} : { employee_id: filterEmployee }));
    } catch (error) {
      toast.error('Neizdevās ielādēt individuālās likmes');
    }
  }, [filterEmployee]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.work_type) { toast.error('Izvēlieties darbinieku un darba veidu'); return; }
    if (form.rate === '' || Number(form.rate) < 0) { toast.error('Ievadiet likmi'); return; }
    setSaving(true);
    try {
      await createEmployeeRate({ employee_id: form.employee_id, work_type: form.work_type, rate: Number(form.rate), valid_from: `${form.valid_from}T00:00:00`, note: form.note || null });
      toast.success('Individuālā likme saglabāta');
      setForm((p) => ({ ...p, rate: '', note: '' }));
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Dzēst individuālo likmi?')) return;
    try { await deleteEmployeeRate(id); load(); } catch (error) { toast.error(error.response?.data?.detail || 'Neizdevās dzēst'); }
  };

  const selectedType = activeTypes.find((w) => w.key === form.work_type);

  return (
    <div className="space-y-4" data-testid="employee-rates-tab">
      <p className="text-sm text-[#2E2E2E]/70 flex items-start gap-2"><UserCog className="w-4 h-4 mt-0.5 shrink-0" /> Individuālā likme aizstāj standarta likmi konkrētam darbiniekam no norādītā datuma. Ja uzdevumā ir ievadīta sava likme, tā ir prioritāra.</p>

      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end rounded-xl bg-[#f8faf8] p-3" data-testid="employee-rate-form">
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Darbinieks</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm((p) => ({ ...p, employee_id: v }))}>
            <SelectTrigger data-testid="employee-rate-employee"><SelectValue placeholder="Izvēlieties" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`employee-rate-employee-${e.id}`}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Darba veids</Label>
          <Select value={form.work_type} onValueChange={(v) => setForm((p) => ({ ...p, work_type: v }))}>
            <SelectTrigger data-testid="employee-rate-work-type"><SelectValue placeholder="Izvēlieties" /></SelectTrigger>
            <SelectContent>{activeTypes.map((w) => <SelectItem key={w.key} value={w.key} data-testid={`employee-rate-work-type-${w.key}`}>{w.label} ({w.unit}) · std {formatEur(w.rate)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Likme (€{selectedType ? ` / ${selectedType.unit}` : ''})</Label><Input type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))} data-testid="employee-rate-rate" /></div>
        <div className="space-y-1"><Label className="text-xs">Spēkā no</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm((p) => ({ ...p, valid_from: e.target.value }))} data-testid="employee-rate-valid-from" /></div>
        <div className="space-y-1 md:col-span-5"><Label className="text-xs">Piezīme / pamatojums</Label><Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} placeholder="piem. pieredzējis darbinieks" data-testid="employee-rate-note" /></div>
        <Button type="submit" disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="employee-rate-submit">Pievienot</Button>
      </form>

      <div className="flex items-center gap-2">
        <Label className="text-xs">Filtrs</Label>
        <Select value={filterEmployee} onValueChange={setFilterEmployee}>
          <SelectTrigger className="w-64" data-testid="employee-rate-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Visi darbinieki</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {rates.length === 0 ? <p className="text-sm text-[#2E2E2E]/60" data-testid="employee-rates-empty">Nav individuālu likmju — visiem tiek piemērotas standarta likmes.</p> : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm" data-testid="employee-rates-table">
            <thead className="bg-[#f8faf8] text-left"><tr><th className="px-4 py-3">Darbinieks</th><th className="px-4 py-3">Darba veids</th><th className="px-4 py-3">Likme</th><th className="px-4 py-3">Spēkā no</th><th className="px-4 py-3">Piezīme</th><th className="px-4 py-3 text-right">Darbības</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rates.map((r) => (
                <tr key={r.id} data-testid={`employee-rate-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.employee_name}</td>
                  <td className="px-4 py-3">{r.work_type_label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatEur(r.rate)} / {r.unit}{r.is_current && <Badge className="ml-2 bg-green-100 text-green-700">spēkā</Badge>}</td>
                  <td className="px-4 py-3">{formatDate(r.valid_from)}</td>
                  <td className="px-4 py-3 text-[#2E2E2E]/60">{r.note || '-'}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`employee-rate-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
